/**
 * Sound, modelled on what the original does with the Game Boy sound chip (measured by logging its
 * register writes during a race — no samples or ROM data are used):
 *  - engine: channel 1, 12.5 % duty pulse, re-triggered every logic tick (~95.7 ms) with a decaying
 *    envelope and a small downward sweep → a "chugging" buzz. Pitch ≈ 126 Hz at 80 km/h to 212 Hz at 280 km/h.
 *  - turbo (jet): channel 1 turns into a high whine (≈1.3 kHz rising to 2.7 kHz while held) and channel 4
 *    adds a bright noise burst on every tick, louder as the tick goes by.
 *  - braking: the engine channel is silenced.
 */
import { volumeGain } from "./options";
import { INTRO, TITLE, VICTORY, TRACKS, Track, chordTone, mtof } from "./music";
const TICK_S = 5.7158 / 59.7275;

function pulseWave(ctx: AudioContext, duty: number): PeriodicWave {
  const N = 64;
  const re = new Float32Array(N + 1), im = new Float32Array(N + 1);
  for (let n = 1; n <= N; n++) {
    re[n] = (2 * Math.sin(2 * Math.PI * n * duty)) / (Math.PI * n);
    im[n] = (2 * (1 - Math.cos(2 * Math.PI * n * duty))) / (Math.PI * n);
  }
  return ctx.createPeriodicWave(re, im);
}

export class Sfx {
  private ctx?: AudioContext;
  private master?: GainNode;
  private eng?: OscillatorNode;
  private engGain?: GainNode;
  private whine?: OscillatorNode;
  private whineGain?: GainNode;
  private noiseBuf?: AudioBuffer;
  private noiseGain?: GainNode;
  private skidGain?: GainNode;
  private carBus?: GainNode; // engine, jet and whine: the "car" volume
  private skidBus?: GainNode; // tyre squeal: the "skid" volume
  private skidFilter?: BiquadFilterNode;
  private kerbGain?: GainNode; // rumble strips: level
  private kerbLfo?: OscillatorNode; // rumble strips: the buzz rate follows the speed
  private lastTick = 0;
  private wasJet = false;
  private jetHz = 1337;
  muted = false;
  /** Web Audio state: mobile browsers keep the context suspended until a real touch/click resumes it. */
  get state(): string {
    return this.ctx?.state ?? "none";
  }
  /**
   * Call from every user-activation event (touchend, pointerup, click, keydown). Creates the context on first use,
   * resumes it, and plays one silent sample, which is what iOS needs to unlock audio output.
   */
  unlock() {
    this.init();
    const ctx = this.ctx;
    if (!ctx) return;
    if (ctx.state !== "running") void ctx.resume().catch(() => {});
    try {
      const src = ctx.createBufferSource();
      src.buffer = ctx.createBuffer(1, 1, 22050);
      src.connect(ctx.destination);
      src.start(0);
    } catch {
      /* ignore */
    }
  }
  musicMuted = false;
  private musicGain?: GainNode;
  private musicTimer?: number;
  private musicStep = 0;
  private musicNext = 0;
  private musicDef: Track = TRACKS[0];
  musicKind: string | null = null;
  private musicOnce = false; // the grid fanfare plays once instead of looping
  private waves: Record<string, PeriodicWave> = {};

  init() {
    if (this.ctx) {
      if (this.ctx.state === "suspended") void this.ctx.resume();
      return;
    }
    try {
      const ctx = (this.ctx = new AudioContext());
      const comp = ctx.createDynamicsCompressor();
      this.master = ctx.createGain();
      this.master.gain.value = 0.7;
      this.master.connect(comp).connect(ctx.destination);
      this.musicGain = ctx.createGain();
      this.musicGain.gain.value = this.musicLevel();
      this.musicGain.connect(this.master);
      this.carBus = ctx.createGain();
      this.carBus.gain.value = volumeGain("car");
      this.carBus.connect(this.master);
      this.skidBus = ctx.createGain();
      this.skidBus.gain.value = volumeGain("skid");
      this.skidBus.connect(this.master);
      this.waves = { lead: pulseWave(ctx, 0.25), bass: pulseWave(ctx, 0.5) };
      const wave = pulseWave(ctx, 0.125);

      this.eng = ctx.createOscillator();
      this.eng.setPeriodicWave(wave);
      this.engGain = ctx.createGain();
      this.engGain.gain.value = 0;
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 3800;
      this.eng.connect(this.engGain).connect(lp).connect(this.carBus);
      this.eng.start();

      this.whine = ctx.createOscillator();
      this.whine.setPeriodicWave(wave);
      this.whineGain = ctx.createGain();
      this.whineGain.gain.value = 0;
      this.whine.connect(this.whineGain).connect(this.carBus);
      this.whine.start();

      const len = ctx.sampleRate;
      this.noiseBuf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = this.noiseBuf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuf;
      src.loop = true;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = 2500;
      this.noiseGain = ctx.createGain();
      this.noiseGain.gain.value = 0;
      src.connect(hp).connect(this.noiseGain).connect(this.carBus);
      src.start();

      // tyre squeal while sliding: band-passed noise whose pitch follows the speed, plus a thin wobbling whistle
      const sk = ctx.createBufferSource();
      sk.buffer = this.noiseBuf;
      sk.loop = true;
      this.skidFilter = ctx.createBiquadFilter();
      this.skidFilter.type = "bandpass";
      this.skidFilter.frequency.value = 1500;
      this.skidFilter.Q.value = 7;
      this.skidGain = ctx.createGain();
      this.skidGain.gain.value = 0;
      sk.connect(this.skidFilter).connect(this.skidGain).connect(this.skidBus);
      sk.start();
      const wh = ctx.createOscillator();
      wh.type = "square";
      wh.frequency.value = 1180;
      const whg = ctx.createGain();
      whg.gain.value = 0.18;
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 9;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 60;
      lfo.connect(lfoGain).connect(wh.frequency);
      wh.connect(whg).connect(this.skidGain);
      wh.start();
      lfo.start();

      // rumble strips: a low buzzing tone chopped at a rate that follows the speed (the stripes go by faster)
      const kt = ctx.createOscillator();
      kt.type = "sawtooth";
      kt.frequency.value = 120; // above 100 Hz so that phone speakers reproduce it
      const klp = ctx.createBiquadFilter();
      klp.type = "lowpass";
      klp.frequency.value = 1400;
      const chop = ctx.createGain();
      chop.gain.value = 0.5;
      this.kerbLfo = ctx.createOscillator();
      this.kerbLfo.type = "square";
      this.kerbLfo.frequency.value = 16;
      const chopDepth = ctx.createGain();
      chopDepth.gain.value = 0.5;
      this.kerbLfo.connect(chopDepth).connect(chop.gain); // the gain swings between 0 and 1
      this.kerbGain = ctx.createGain();
      this.kerbGain.gain.value = 0;
      kt.connect(klp).connect(chop).connect(this.kerbGain).connect(this.carBus);
      kt.start();
      this.kerbLfo.start();
    } catch {
      this.ctx = undefined;
    }
  }

  /** Debug/test hook: an analyser tapped on the master bus. */
  tap(): AnalyserNode | undefined {
    if (!this.ctx || !this.master) return undefined;
    const a = this.ctx.createAnalyser();
    a.fftSize = 8192;
    this.master.connect(a);
    return a;
  }

  toggleMusic() {
    this.musicMuted = !this.musicMuted;
    this.applyVolumes();
  }

  private musicLevel() {
    return this.musicMuted ? 0 : 0.32 * volumeGain("music");
  }
  /** Applies the player's volume settings (options screen). */
  applyVolumes() {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime;
    this.musicGain?.gain.setTargetAtTime(this.musicLevel(), t, 0.05);
    this.carBus?.gain.setTargetAtTime(volumeGain("car"), t, 0.05);
    this.skidBus?.gain.setTargetAtTime(volumeGain("skid"), t, 0.05);
  }
  /** A short sample of the car or tyre sound so that a new volume can be judged by ear (the music is already playing). */
  preview(kind: "car" | "skid") {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    if (kind === "car" && this.carBus) {
      const o = ctx.createOscillator();
      o.setPeriodicWave(pulseWave(ctx, 0.125));
      o.frequency.setValueAtTime(150, t);
      o.frequency.linearRampToValueAtTime(190, t + 0.5);
      g.gain.linearRampToValueAtTime(0.2, t + 0.02);
      g.gain.setValueAtTime(0.2, t + 0.45);
      g.gain.linearRampToValueAtTime(0, t + 0.5);
      o.connect(g).connect(this.carBus);
      o.start(t);
      o.stop(t + 0.55);
    } else if (kind === "skid" && this.skidBus && this.noiseBuf) {
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuf;
      const f = ctx.createBiquadFilter();
      f.type = "bandpass";
      f.frequency.value = 1700;
      f.Q.value = 7;
      g.gain.linearRampToValueAtTime(0.1, t + 0.03);
      g.gain.setValueAtTime(0.1, t + 0.5);
      g.gain.linearRampToValueAtTime(0, t + 0.6);
      src.connect(f).connect(g).connect(this.skidBus);
      src.start(t);
      src.stop(t + 0.65);
    }
  }

  get musicPlaying() {
    return this.musicTimer !== undefined;
  }

  /** kind: "title" or a race track index. Restarts only when the requested tune changes. */
  startMusic(kind: "title" | "intro" | "victory" | number) {
    if (!this.ctx) return;
    const key = String(kind);
    if ((kind === "intro" || kind === "victory") && this.musicKind === key) return; // already played for this start
    if (this.musicTimer !== undefined) {
      if (this.musicKind === key) return;
      this.stopMusic();
    }
    this.musicOnce = kind === "intro" || kind === "victory";
    this.musicDef = kind === "title" ? TITLE : kind === "intro" ? INTRO : kind === "victory" ? VICTORY : TRACKS[((kind % TRACKS.length) + TRACKS.length) % TRACKS.length];
    this.musicKind = key;
    this.musicStep = 0;
    this.musicNext = this.ctx.currentTime + 0.08;
    this.musicTimer = window.setInterval(() => this.pump(), 25);
  }

  stopMusic() {
    if (this.musicTimer !== undefined) clearInterval(this.musicTimer);
    this.musicTimer = undefined;
    this.musicKind = null;
  }

  /** Look-ahead scheduler: queues 16th-note events slightly ahead of the audio clock. */
  private pump() {
    const ctx = this.ctx;
    if (!ctx) return;
    const tr = this.musicDef;
    const step = 60 / tr.bpm / 4;
    while (this.musicNext < ctx.currentTime + 0.15) {
      const bars = tr.roots.length;
      if (this.musicOnce && this.musicStep >= bars * 16) {
        clearInterval(this.musicTimer); // fanfare over: stay silent until the race tune starts (musicKind stays "intro", so it is not replayed)
        this.musicTimer = undefined;
        return;
      }
      const i = this.musicStep % (bars * 16);
      const bar = Math.floor(i / 16), s = i % 16, t = this.musicNext;
      const li = tr.lead[bar][s];
      if (li >= 0) this.note("lead", mtof(chordTone(tr.roots[bar], tr.minor[bar], li)), t, step * 0.85, 0.16);
      const bo = tr.bassOct[s];
      if (bo >= 0 && s % 2 === 0) this.note("bass", mtof(tr.roots[bar] - 12 + bo), t, step * 1.7, 0.3);
      const d = tr.drums[s];
      if (d === "k") this.drum(t, 0.5, 160, 0.12);
      else if (d === "s") this.drum(t, 0.35, 1800, 0.09);
      else if (d === "h") this.drum(t, 0.12, 7000, 0.03);
      this.musicNext += step;
      this.musicStep++;
    }
  }

  private note(kind: "lead" | "bass", hz: number, t: number, dur: number, vol: number) {
    const ctx = this.ctx;
    if (!ctx || !this.musicGain) return;
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.setPeriodicWave(this.waves[kind]);
    o.frequency.setValueAtTime(hz, t);
    g.gain.setValueAtTime(vol, t);
    g.gain.linearRampToValueAtTime(vol * (kind === "lead" ? 0.45 : 0.7), t + dur);
    g.gain.linearRampToValueAtTime(0, t + dur + 0.015);
    o.connect(g).connect(this.musicGain);
    o.start(t);
    o.stop(t + dur + 0.03);
  }

  private drum(t: number, vol: number, hz: number, dur: number) {
    const ctx = this.ctx;
    if (!ctx || !this.noiseBuf || !this.musicGain) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const f = ctx.createBiquadFilter();
    f.type = hz < 400 ? "lowpass" : hz > 4000 ? "highpass" : "bandpass";
    f.frequency.value = hz;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(f).connect(g).connect(this.musicGain);
    src.start(t, Math.random() * 0.5);
    src.stop(t + dur + 0.02);
    if (hz < 400) {
      // kick: add a short falling sine thump
      const o = ctx.createOscillator(), og = ctx.createGain();
      o.frequency.setValueAtTime(140, t);
      o.frequency.exponentialRampToValueAtTime(45, t + 0.1);
      og.gain.setValueAtTime(0.6, t);
      og.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
      o.connect(og).connect(this.musicGain);
      o.start(t);
      o.stop(t + 0.14);
    }
  }

  toggleMute() {
    this.muted = !this.muted;
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(this.muted ? 0 : 0.7, this.ctx.currentTime, 0.02);
  }

  /** Called once per video frame. */
  get skidLevel(): number {
    return this.skidGain?.gain.value ?? 0;
  }

  /** Tyre squeal: call every frame; `on` while the car slides in a curve (20° pose), `speed` in km/h. */
  skid(on: boolean, speed = 0) {
    const ctx = this.ctx;
    if (!ctx || !this.skidGain || !this.skidFilter) return;
    const t = ctx.currentTime;
    if (!on) return void this.skidGain.gain.setTargetAtTime(0, t, 0.06);
    this.skidFilter.frequency.setTargetAtTime(1000 + speed * 2.4, t, 0.1);
    this.skidGain.gain.setTargetAtTime(0.035 + 0.07 * Math.min(1, speed / 320), t, 0.04);
  }

  /** Level of the rumble-strip buzz (debug/test hook). */
  get kerbLevel(): number {
    return this.kerbGain?.gain.value ?? 0;
  }

  /** Rumble strips: call every frame; `on` while the car runs along the edge of the track, `speed` in km/h. */
  kerb(on: boolean, speed = 0) {
    const ctx = this.ctx;
    if (!ctx || !this.kerbGain || !this.kerbLfo) return;
    const t = ctx.currentTime;
    if (!on) return void this.kerbGain.gain.setTargetAtTime(0, t, 0.04);
    this.kerbLfo.frequency.setTargetAtTime(8 + speed * 0.07, t, 0.05);
    this.kerbGain.gain.setTargetAtTime(0.16 + 0.34 * Math.min(1, speed / 300), t, 0.03);
  }

  engine(speed: number, on: boolean, jet: boolean, brake = false) {
    const ctx = this.ctx;
    if (!ctx || !this.eng || !this.engGain || !this.whine || !this.whineGain || !this.noiseGain) return;
    const t = ctx.currentTime;
    if (!on) {
      this.engGain.gain.setTargetAtTime(0, t, 0.05);
      this.whineGain.gain.setTargetAtTime(0, t, 0.05);
      this.noiseGain.gain.setTargetAtTime(0, t, 0.05);
      this.wasJet = false;
      return;
    }
    if (jet && !this.wasJet) {
      this.jetHz = 1337;
      this.lastTick = 0; // retrigger straight away
      this.burst(0.22, 0.12, 2000, this.carBus); // the jet ignition belongs to the car volume
    }
    if (!jet && this.wasJet) {
      this.whineGain.gain.cancelScheduledValues(t);
      this.noiseGain.gain.cancelScheduledValues(t);
      this.whineGain.gain.setTargetAtTime(0, t, 0.03);
      this.noiseGain.gain.setTargetAtTime(0, t, 0.03);
    }
    this.wasJet = jet;

    if (t - this.lastTick >= TICK_S) {
      this.lastTick = t;
      const f = Math.max(70, 126 + (speed - 80) * 0.43);
      const end = t + TICK_S * 0.92;
      // engine: re-triggered pulse with decaying volume and a small downward sweep
      const vol = brake ? 0 : jet ? 0.1 : 0.2;
      this.engGain.gain.cancelScheduledValues(t);
      this.engGain.gain.setValueAtTime(vol, t);
      this.engGain.gain.linearRampToValueAtTime(vol * 0.3, end);
      this.eng.frequency.cancelScheduledValues(t);
      this.eng.frequency.setValueAtTime(f, t);
      this.eng.frequency.linearRampToValueAtTime(f * 0.96, end);
      if (jet) {
        this.jetHz = Math.min(2730, this.jetHz * 1.025);
        this.whine.frequency.setValueAtTime(this.jetHz, t);
        this.whineGain.gain.cancelScheduledValues(t);
        this.whineGain.gain.setValueAtTime(0.06, t);
        this.whineGain.gain.linearRampToValueAtTime(0.02, end);
        // noise burst that swells over the tick (the original's "increasing" envelope)
        this.noiseGain.gain.cancelScheduledValues(t);
        this.noiseGain.gain.setValueAtTime(0.05, t);
        this.noiseGain.gain.linearRampToValueAtTime(0.2, end);
      }
    }
  }

  private burst(vol: number, dur: number, hz: number, bus?: AudioNode) {
    const ctx = this.ctx;
    if (!ctx || !this.noiseBuf || !this.master) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const f = ctx.createBiquadFilter();
    f.type = "bandpass";
    f.frequency.value = hz;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    src.connect(f).connect(g).connect(bus ?? this.master);
    src.start();
    src.stop(ctx.currentTime + dur + 0.02);
  }

  crash() {
    this.burst(0.5, 0.35, 500);
    this.beep(90, 0.2);
  }

  beep(freq: number, dur = 0.15) {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.setPeriodicWave(pulseWave(ctx, 0.5));
    o.frequency.value = freq;
    g.gain.setValueAtTime(0.08, ctx.currentTime);
    g.gain.setTargetAtTime(0, ctx.currentTime + dur * 0.7, 0.03);
    o.connect(g).connect(this.master);
    o.start();
    o.stop(ctx.currentTime + dur);
  }

  /** Short rising arpeggio: lap completed / race finished. */
  jingle(long = false) {
    const ctx = this.ctx;
    if (!ctx) return;
    const notes = long ? [523, 659, 784, 1047, 784, 1047] : [659, 880];
    notes.forEach((n, i) => setTimeout(() => this.beep(n, 0.11), i * 110));
  }
}
